import { api } from "../../lib/api";
import { formatDateWithTimezoneOffset } from "../../utils/dateFormatter";
export const getChargerDetails = async (chargerId, projectId, timezoneOffset) => {
    // Calculate today in the project's timezone
    const getCurrentDateInTimezone = () => {
        const now = new Date();
        if (!timezoneOffset)
            return now;
        // Extract offset hours from format like "UTC+01:00" or "+09:00"
        const offsetStr = timezoneOffset.replace("UTC", "");
        const offsetMatch = offsetStr.match(/([+-])(\d{1,2}):?(\d{2})?/);
        if (offsetMatch) {
            const sign = offsetMatch[1] === "+" ? 1 : -1;
            const hours = parseInt(offsetMatch[2], 10);
            const minutes = parseInt(offsetMatch[3] || "0", 10);
            const totalOffsetMinutes = sign * (hours * 60 + minutes);
            // Get UTC time and apply project timezone offset
            const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
            const projectTime = new Date(utcTime + totalOffsetMinutes * 60000);
            return projectTime;
        }
        return now;
    };
    // Helper to create a UTC date representing a specific time in the project timezone
    const createProjectDate = (year, month, day, hour = 0, minute = 0, second = 0, ms = 0) => {
        // Create date string in ISO format for the project timezone
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
        // Parse as UTC so getUTC* methods return the correct values
        return new Date(dateStr + "Z");
    };
    const today = getCurrentDateInTimezone();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    const todayDay = today.getDate();
    // Get today's date in project's timezone (start of day 00:00:00)
    const startOfToday = createProjectDate(todayYear, todayMonth, todayDay, 0, 0, 0, 0);
    // Get end of today (23:59:59.999)
    const endOfToday = createProjectDate(todayYear, todayMonth, todayDay, 23, 59, 59, 999);
    const fromTime = formatDateWithTimezoneOffset(startOfToday, timezoneOffset);
    const toTime = formatDateWithTimezoneOffset(endOfToday, timezoneOffset);
    const { data } = await api.post(`/projects/${projectId}/chargers/search`, {
        filters: [
            {
                propertyName: "assetSourceId",
                type: "stringEquals",
                values: [chargerId]
            },
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: false,
                    left: fromTime,
                    right: toTime
                },
                type: "timeRange"
            }
        ]
    });
    return data;
};
export const getChargerTransactions = async (assetId, projectId, queryParams) => {
    // Build filters array conditionally
    const filters = queryParams?.fromTime && queryParams?.toTime
        ? [
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: false,
                    left: queryParams.fromTime,
                    right: queryParams.toTime
                },
                type: "timeRange"
            }
        ]
        : [];
    const { data } = await api.post(`/projects/${projectId}/chargers/${assetId}/transactions`, {
        filters,
        ...(queryParams?.limit && { limit: parseInt(queryParams.limit) }),
        ...(queryParams?.skip && { skip: parseInt(queryParams.skip) }),
        ...(queryParams?.nextCursor && { cursor: queryParams.nextCursor }),
        ...(queryParams?.sortBy && { sortBy: [queryParams.sortBy] }),
        ...(queryParams?.orderBy && { orderBy: [queryParams.orderBy] })
    });
    return data;
};
export const getChargerTransactionsCount = async (assetId, projectId, queryParams) => {
    // Build filters array conditionally
    const filters = queryParams?.fromTime && queryParams?.toTime
        ? [
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: false,
                    left: queryParams.fromTime,
                    right: queryParams.toTime
                },
                type: "timeRange"
            }
        ]
        : [];
    const { data } = await api.post(`/projects/${projectId}/chargers/${assetId}/transactions/COUNT`, {
        filters
    });
    return data;
};
export const getChargerHistory = async (assetId, projectId, queryParams) => {
    // Build filters array conditionally
    const filters = queryParams?.fromTime && queryParams?.toTime
        ? [
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: false,
                    left: queryParams.fromTime,
                    right: queryParams.toTime
                },
                type: "timeRange"
            }
        ]
        : [];
    const { data } = await api.post(`/projects/${projectId}/chargers/${assetId}/status/history`, {
        filters
    });
    return data;
};
export const getChargerConnectorsEnergyHistogram = async (assetId, projectId, queryParams) => {
    // Build filters array conditionally
    const filters = queryParams?.fromTime && queryParams?.toTime
        ? [
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: false,
                    left: queryParams.fromTime,
                    right: queryParams.toTime
                },
                type: "timeRange"
            }
        ]
        : [];
    const { data } = await api.post(`/projects/${projectId}/chargers/${assetId}/connectors/energy/histogram`, {
        filters
    });
    return data;
};
export const getChargerTransactionList = async (chargerTransactionSearchRequest, queryParams) => {
    const allData = [];
    //default limit value is 20
    const limit = queryParams?.limit ? queryParams.limit : 20;
    // skip is offset value
    let offset = 0;
    const params = new URLSearchParams();
    params.append("limit", limit.toString());
    params.append("skip", offset.toString());
    let nextCursor = undefined;
    do {
        const { data } = await api.post(`/projects/${queryParams?.projectId}/chargers/${queryParams?.assetId}/transactions${params && "?" + params.toString()}`, chargerTransactionSearchRequest);
        // check and avoid infinite loop
        if (data.responseMetadata?.nextCursor && nextCursor === data.responseMetadata?.nextCursor) {
            throw new Error("Infinite loop error");
        }
        allData.push(data);
        nextCursor = data.responseMetadata?.nextCursor;
        params.set("cursor", nextCursor || "");
        offset += limit;
        params.set("skip", offset.toString());
    } while (nextCursor);
    return allData;
};
